import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { formatPeriod } from "@/lib/resumeUtils";
import WizardModal from "@/components/resume/WizardModal";
import ThinkingLoader from "@/components/ThinkingLoader";
import { Sparkles, ArrowLeft, ArrowRight, Globe, Check, Star, Palette } from "lucide-react";
import { toast } from "sonner";

export default function LetterWizard({ prefillJob, onClose, onDone }) {
  const { t, lang } = useT();
  // Draft persistence — the wizard is multi-step and users often close the
  // tab mid-way. We snapshot the essential form state to localStorage on
  // every change and restore on mount, unless the wizard was opened via
  // `prefillJob` (in which case the incoming job wins).
  const DRAFT_KEY = "jobgrid.letter-wizard.draft.v1";
  const draftRestore = (() => {
    if (prefillJob?.id) return null;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      // Ignore drafts older than 7 days — feels stale.
      if (obj?.savedAt && Date.now() - obj.savedAt > 7 * 24 * 60 * 60 * 1000) return null;
      return obj;
    } catch { return null; }
  })();

  const [step, setStep] = useState(draftRestore?.step || 1);
  // Step 1 — vacancy
  const [jobUrl, setJobUrl] = useState(draftRestore?.jobUrl || prefillJob?.url || "");
  const [jobTitle, setJobTitle] = useState(draftRestore?.jobTitle || prefillJob?.job_title || "");
  const [company, setCompany] = useState(draftRestore?.company || prefillJob?.company || "");
  const [jobDescription, setJobDescription] = useState(draftRestore?.jobDescription || prefillJob?.description || "");
  const [scraping, setScraping] = useState(false);
  // Step 2 — resume
  const [resumes, setResumes] = useState(null);
  const [resumeId, setResumeId] = useState(draftRestore?.resumeId || null);
  const [resumeDetail, setResumeDetail] = useState(null);
  // Step 3 — language + style template
  const [letterLang, setLetterLang] = useState(draftRestore?.letterLang || lang);
  const [langAutoDetected, setLangAutoDetected] = useState(false);
  const [langUserOverridden, setLangUserOverridden] = useState(!!draftRestore?.langUserOverridden);
  const [detectingLang, setDetectingLang] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(draftRestore?.templateId || null);
  // Steps 4/5 — highlights
  const [selExp, setSelExp] = useState(Array.isArray(draftRestore?.selExp) ? draftRestore.selExp : []);
  const [selSkills, setSelSkills] = useState(Array.isArray(draftRestore?.selSkills) ? draftRestore.selSkills : []);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);   // null | "timeout" | "generic"
  const [errorDetail, setErrorDetail] = useState("");
  const [autoRetrying, setAutoRetrying] = useState(false);
  const [generateStartedAt, setGenerateStartedAt] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  // Existing-job picker (step 1 tab): populated once, filtered client-side.
  const [existingJobs, setExistingJobs] = useState([]);
  const [pickedJobId, setPickedJobId] = useState(draftRestore?.pickedJobId || prefillJob?.id || "");
  const [jobSourceMode, setJobSourceMode] = useState(draftRestore?.jobSourceMode || (prefillJob?.id ? "existing" : "new"));
  const [jobSearch, setJobSearch] = useState("");

  // If a draft was restored, quietly let the user know so they don't
  // wonder where the input came from.
  useEffect(() => {
    if (draftRestore) {
      toast.info(t("ltwDraftRestored"), {
        duration: 5000,
        action: {
          label: t("ltwDraftClear"),
          onClick: () => {
            try { localStorage.removeItem(DRAFT_KEY); } catch {}
            window.location.reload();
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist draft on every meaningful change. We serialize a compact
  // snapshot (no resume/template lists — those are fetched from server).
  // Debounce is unnecessary: JSON.stringify + localStorage.setItem is
  // sub-millisecond for this payload size and localStorage is synchronous
  // but very cheap in modern browsers.
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        savedAt: Date.now(),
        step,
        jobUrl, jobTitle, company, jobDescription,
        jobSourceMode, pickedJobId,
        resumeId, letterLang, langUserOverridden, templateId,
        selExp, selSkills,
      }));
    } catch {}
  }, [step, jobUrl, jobTitle, company, jobDescription, jobSourceMode,
      pickedJobId, resumeId, letterLang, langUserOverridden, templateId,
      selExp, selSkills]);

  useEffect(() => {
    api.get("/cv-templates").then(({ data }) => setTemplates(data)).catch(() => {});
    api.get("/jobs").then(({ data }) => setExistingJobs(Array.isArray(data) ? data : [])).catch(() => setExistingJobs([]));
    api.get("/resumes").then(({ data }) => {
      setResumes(data);
      // Preselect the default CV so the letter always starts from a chosen CV
      const def = data.find((v) => v.is_default) || data[0];
      if (def) pickResume(def.id);
    }).catch(() => setResumes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrape = async () => {
    if (!jobUrl.trim()) { toast.error(t("addUrlFirst")); return; }
    setScraping(true);
    try {
      const { data } = await api.post("/scrape", { url: jobUrl.trim() });
      if (data.success) {
        if (data.title && !jobTitle) setJobTitle(data.title);
        if (data.company && !company) setCompany(data.company);
        if (data.description) setJobDescription(data.description);
        toast.success(t("ltwScrapeOk"));
      } else {
        toast.error(data.error || t("ltwScrapeFailed"));
      }
    } catch {
      toast.error(t("ltwScrapeFailed"));
    } finally {
      setScraping(false);
    }
  };

  const pickResume = useCallback(async (id) => {
    setResumeId(id);
    setSelExp([]);
    try {
      const { data } = await api.get(`/resumes/${id}`);
      setResumeDetail(data);
      // Auto-highlight skills that appear in the vacancy text (better AI match)
      const desc = (jobDescription || "").toLowerCase();
      const matched = (data.skills || [])
        .filter((s) => s.name && desc.includes(s.name.toLowerCase()))
        .map((s) => s.name);
      setSelSkills(matched);
    } catch {
      setResumeDetail(null);
      setSelSkills([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDescription]);

  const next = () => {
    if (step === 1) {
      if (!jobTitle.trim() || !company.trim()) { toast.error(t("ltwRequired")); return; }
      setStep(2);
    } else if (step === 2) {
      if (!resumeId) { toast.error(t("ltwPickResumeFirst")); return; }
      setStep(3);
    } else {
      setStep(step + 1);
    }
  };

  // Auto-detect the vacancy language once when the user reaches step 3 with a
  // filled description. We never overwrite an explicit user choice.
  useEffect(() => {
    if (step !== 3 || langAutoDetected || langUserOverridden) return;
    const text = (jobDescription || "").trim();
    if (text.length < 40) return;
    let active = true;
    setDetectingLang(true);
    api.post("/letters/detect-lang", { text })
      .then(({ data }) => {
        if (!active) return;
        const code = (data?.lang || "").toLowerCase();
        if (["nl", "en", "de", "fr", "es"].includes(code)) {
          setLetterLang(code);
        }
      })
      .catch(() => { /* silent — user can still choose manually */ })
      .finally(() => {
        if (active) { setDetectingLang(false); setLangAutoDetected(true); }
      });
    return () => { active = false; };
  }, [step, jobDescription, langAutoDetected, langUserOverridden]);

  const toggle = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

  const pickExistingJob = (job) => {
    setPickedJobId(job.id);
    setJobUrl(job.job_url || "");
    setJobTitle(job.job_title || "");
    setCompany(job.company || "");
    setJobDescription(job.description || "");
  };

  // Small helper: is this error a transient/timeout error we can retry silently?
  // Includes the full Cloudflare 5xx family:
  //   520 — "origin returned invalid response" (empty/malformed body — the
  //         one the user is most likely to see on jobgrid.nl behind CF when
  //         our backend hits a transient hiccup mid-response)
  //   521 — "web server is down" (upstream connect refused)
  //   522 — "connection timed out" (upstream TCP timeout)
  //   524 — "a timeout occurred" (upstream held connection > 100s)
  //   525 — "SSL handshake failed" (rare, transient)
  //   502/503/504 — standard bad gateway / unavailable / gateway timeout
  // Any of these are worth one silent retry: the AI call is idempotent from
  // the client's perspective (the wizard hasn't committed anything yet) and
  // the second attempt usually succeeds within a few seconds.
  const isTransientError = (err) => {
    const status = err?.response?.status;
    const code = err?.code;
    return (
      status === 520 || status === 521 || status === 522 || status === 524 || status === 525 ||
      status === 502 || status === 503 || status === 504 ||
      code === "ECONNABORTED" || code === "ERR_NETWORK" ||
      /timeout|network/i.test(err?.message || "")
    );
  };

  const generate = async () => {
    setGenerating(true);
    setGenerateError(null);
    setErrorDetail("");
    setAutoRetrying(false);
    setGenerateStartedAt(Date.now());
    setElapsedSec(0);
    const payload = {
      job_title: jobTitle.trim(),
      company: company.trim(),
      job_description: jobDescription,
      job_url: jobUrl.trim(),
      job_id: pickedJobId || prefillJob?.id || "",
      resume_id: resumeId,
      lang: letterLang,
      template_id: templateId || "",
      highlight_experience_ids: selExp,
      highlight_skills: selSkills,
    };
    // Silent auto-retry: try up to 2 times before surfacing a retry panel.
    // Cloudflare 524's are almost always transient — a fresh call typically
    // completes within a few seconds. The user only sees the retry-panel
    // if BOTH attempts fail.
    let lastErr = null;
    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        const { data } = await api.post("/letters/generate", payload, { timeout: 150000 });
        const backend = process.env.REACT_APP_BACKEND_URL;
        const letterUrl = `${backend}/api/letters/${data.id}/pdf?doc=letter#toolbar=0`;
        const validationUrl = `${backend}/api/letters/${data.id}/pdf?doc=validation#toolbar=0`;
        toast.success(t("ltwGenerated"), {
          duration: 15000,
          description: data.job_id && !pickedJobId && !prefillJob?.id
            ? t("ltwGeneratedWithJob")
            : undefined,
          action: {
            label: t("ltwOpenLetter"),
            onClick: () => window.open(letterUrl, "_blank", "noopener"),
          },
        });
        toast.info(t("ltwValidationReady"), {
          duration: 15000,
          action: {
            label: t("ltwOpenValidation"),
            onClick: () => window.open(validationUrl, "_blank", "noopener"),
          },
        });
        // Success → wipe the local draft so the next open starts fresh.
        try { localStorage.removeItem(DRAFT_KEY); } catch {}
        setGenerating(false);
        setAutoRetrying(false);
        onDone(data);
        return;
      } catch (err) {
        lastErr = err;
        if (attempt === 1 && isTransientError(err)) {
          // First failure was transient → attempt one silent retry.
          setAutoRetrying(true);
          setGenerateStartedAt(Date.now()); // reset elapsed timer for retry
          await new Promise((r) => setTimeout(r, 800));
          continue;
        }
        break;
      }
    }
    // Both attempts failed — decide whether to show the friendly retry
    // panel (transient) or the raw error toast (real server issue).
    const timedOut = isTransientError(lastErr);
    setGenerateError(timedOut ? "timeout" : "generic");
    // Extract a safe, short error hint. Cloudflare 520-family responses
    // return an HTML body, so `data` can be a big HTML blob without any
    // `.detail` field — we must never surface that verbatim to the user.
    // Priority: server-supplied `detail` (string) → axios `err.message`
    // → status code fallback. Anything else (object, array, HTML text)
    // is collapsed to the status message.
    let hint = "";
    const rawDetail = lastErr?.response?.data?.detail;
    if (typeof rawDetail === "string") {
      hint = rawDetail.slice(0, 200);
    } else if (typeof lastErr?.message === "string") {
      hint = lastErr.message.slice(0, 200);
    }
    setErrorDetail(hint);
    if (!timedOut) {
      toast.error(typeof rawDetail === "string" ? rawDetail : t("ltwFailed"));
    }
    setGenerating(false);
    setAutoRetrying(false);
  };

  const stepTitles = [t("ltwStep1"), t("ltwStep2"), t("ltwStep3"), t("ltwStep4"), t("ltwStep5")];

  // Elapsed-time ticker for the "AI is working" panel — updates every 500ms
  // so the user sees the seconds counting up. Combined with the rotating
  // ThinkingLoader copy, this makes long AI waits feel intentional rather
  // than frozen. Auto-stops when `generating` flips back to false.
  useEffect(() => {
    if (!generating || !generateStartedAt) { setElapsedSec(0); return; }
    const id = setInterval(() => {
      setElapsedSec(Math.max(0, Math.floor((Date.now() - generateStartedAt) / 1000)));
    }, 500);
    return () => clearInterval(id);
  }, [generating, generateStartedAt]);

  // Which of the three high-level stages should be highlighted right now.
  // Rough anchors based on average wallclock (fast model, gemini-3-flash):
  //   0-5s  → "letter"      Brief opstellen
  //   5-10s → "validation"  Eisen valideren
  //   10s+  → "score"       Match-score berekenen & afronden
  const generateStage = elapsedSec < 5 ? "letter" : elapsedSec < 10 ? "validation" : "score";

  return (
    <WizardModal testId="letter-wizard" title={t("ltwTitle")} onClose={onClose} wide>
      <div className="flex items-center justify-between mb-5">
        <p className="font-serif text-xl italic text-neutral-800">{stepTitles[step - 1]}</p>
        <p className="ed-eyebrow" data-testid="letter-wizard-step">{t("ltwStepLabel", { n: step })}</p>
      </div>

      {step === 1 && (
        <div className="space-y-4">
          {/* Source mode toggle: new URL vs existing application */}
          <div className="inline-flex border border-neutral-300" role="tablist" aria-label={t("ltwSourceMode")}>
            <button
              data-testid="letter-wizard-source-new"
              role="tab"
              aria-selected={jobSourceMode === "new"}
              onClick={() => { setJobSourceMode("new"); setPickedJobId(""); }}
              className={`px-4 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors ${jobSourceMode === "new" ? "bg-neutral-900 text-[#F9F8F6]" : "hover:bg-neutral-50"}`}
            >
              {t("ltwSourceNew")}
            </button>
            <button
              data-testid="letter-wizard-source-existing"
              role="tab"
              aria-selected={jobSourceMode === "existing"}
              onClick={() => setJobSourceMode("existing")}
              disabled={existingJobs.length === 0}
              className={`px-4 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors ${jobSourceMode === "existing" ? "bg-neutral-900 text-[#F9F8F6]" : "hover:bg-neutral-50 disabled:opacity-40"}`}
            >
              {t("ltwSourceExisting")} ({existingJobs.length})
            </button>
          </div>

          {jobSourceMode === "existing" ? (
            <div className="space-y-3">
              <input
                data-testid="letter-wizard-job-search"
                className="ed-input"
                placeholder={t("ltwSearchJob")}
                value={jobSearch}
                onChange={(e) => setJobSearch(e.target.value)}
              />
              <div className="border border-neutral-300 divide-y divide-neutral-200 max-h-72 overflow-y-auto">
                {existingJobs
                  .filter((j) => {
                    const q = jobSearch.toLowerCase().trim();
                    if (!q) return true;
                    return (`${j.job_title} ${j.company}`).toLowerCase().includes(q);
                  })
                  .slice(0, 100)
                  .map((j, idx) => {
                    const on = pickedJobId === j.id;
                    return (
                      <button
                        key={j.id}
                        type="button"
                        data-testid={`letter-wizard-job-${idx}`}
                        onClick={() => pickExistingJob(j)}
                        className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 transition-colors ${on ? "bg-neutral-900 text-[#F9F8F6]" : "hover:bg-neutral-50"}`}
                      >
                        <span className="min-w-0">
                          <p className="font-serif text-sm truncate">{j.job_title || "—"}</p>
                          <p className={`text-[0.65rem] font-mono uppercase tracking-[0.12em] mt-0.5 ${on ? "text-neutral-300" : "text-neutral-500"}`}>
                            {j.company || "—"}
                          </p>
                        </span>
                        {j.status && (
                          <span className={`text-[0.6rem] font-mono uppercase tracking-[0.1em] shrink-0 ${on ? "text-neutral-300" : "text-neutral-400"}`}>
                            {j.status}
                          </span>
                        )}
                      </button>
                    );
                  })}
              </div>
              {pickedJobId && (
                <p className="text-xs text-neutral-500 font-mono">
                  {t("ltwPickedJob")}: {jobTitle} — {company}
                </p>
              )}
            </div>
          ) : (
            <>
              <div>
                <label className="ed-label">{t("ltwUrl")}</label>
                <div className="flex gap-2">
                  <input
                    data-testid="letter-wizard-url"
                    className="ed-input"
                    placeholder="https://…"
                    value={jobUrl}
                    onChange={(e) => setJobUrl(e.target.value)}
                  />
                  <button data-testid="letter-wizard-scrape" className="ed-btn ed-btn-outline shrink-0" onClick={scrape} disabled={scraping}>
                    {scraping ? t("ltwScraping") : t("ltwScrape")}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="ed-label">{t("ltwJobTitle")}</label>
                  <input data-testid="letter-wizard-jobtitle" className="ed-input" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
                </div>
                <div>
                  <label className="ed-label">{t("ltwCompany")}</label>
                  <input data-testid="letter-wizard-company" className="ed-input" value={company} onChange={(e) => setCompany(e.target.value)} />
                </div>
              </div>
              <div>
                <label className="ed-label">{t("ltwDescription")}</label>
                <textarea
                  data-testid="letter-wizard-description"
                  className="ed-input min-h-[8rem]"
                  placeholder={t("ltwDescriptionPh")}
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                />
              </div>
            </>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <p className="text-sm text-neutral-600">{t("ltwPickResume")}</p>
          {resumes === null ? (
            <p className="ed-eyebrow">{t("loading")}</p>
          ) : resumes.length === 0 ? (
            <p className="text-sm text-neutral-500 italic font-serif" data-testid="letter-wizard-no-resumes">{t("ltwNoResumes")}</p>
          ) : (
            resumes.map((v, i) => (
              <button
                key={v.id}
                data-testid={`letter-wizard-resume-${i}`}
                onClick={() => pickResume(v.id)}
                className={`w-full text-left ed-card p-4 flex items-center justify-between gap-3 transition-colors ${resumeId === v.id ? "border-neutral-900 bg-white" : "hover:border-neutral-400"}`}
              >
                <span className="min-w-0">
                  <span className="font-serif text-lg block truncate">
                    {v.name}
                    {v.is_default && (
                      <span className="inline-flex items-center gap-1 ml-2 align-middle bg-neutral-900 text-[#F9F8F6] font-mono text-[0.55rem] uppercase tracking-[0.15em] px-2 py-0.5">
                        <Star size={9} strokeWidth={2} fill="currentColor" /> {t("rbDefault")}
                      </span>
                    )}
                  </span>
                  <span className="font-mono text-[0.65rem] text-neutral-500 block truncate">
                    {v.personal?.full_name || "—"}{v.personal?.title ? ` · ${v.personal.title}` : ""}
                  </span>
                </span>
                {resumeId === v.id && <Check size={16} strokeWidth={2} className="shrink-0" />}
              </button>
            ))
          )}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <p className="text-sm text-neutral-600">{t("ltwLang")}</p>
          <div className="flex items-center gap-3">
            <Globe size={16} strokeWidth={1.5} className="text-neutral-500 shrink-0" />
            <select
              data-testid="letter-wizard-lang"
              className="ed-input font-serif text-lg"
              value={letterLang}
              onChange={(e) => { setLetterLang(e.target.value); setLangUserOverridden(true); }}
            >
              <option value="nl">{t("ltwLangNl")}</option>
              <option value="en">{t("ltwLangEn")}</option>
              <option value="de">{t("ltwLangDe")}</option>
              <option value="fr">{t("ltwLangFr")}</option>
              <option value="es">{t("ltwLangEs")}</option>
            </select>
          </div>
          {detectingLang && (
            <p className="font-mono text-[0.6rem] text-neutral-500" data-testid="letter-wizard-lang-detecting">
              {t("ltwLangDetecting")}
            </p>
          )}
          {!detectingLang && langAutoDetected && !langUserOverridden && (
            <p className="font-mono text-[0.6rem] text-neutral-500" data-testid="letter-wizard-lang-autohint">
              {t("ltwLangAutoDetected")}
            </p>
          )}
          {templates.length > 0 && (
            <div className="pt-3">
              <p className="text-sm text-neutral-600 mb-1 flex items-center gap-1.5">
                <Palette size={13} strokeWidth={1.5} /> {t("ltwTemplate")}
              </p>
              <p className="text-xs font-mono text-neutral-500 mb-2">{t("ltwTemplateHint")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  data-testid="letter-wizard-template-default"
                  onClick={() => setTemplateId(null)}
                  className={`ed-card px-4 py-3 text-left flex items-center gap-3 transition-colors ${!templateId ? "border-neutral-900 bg-white" : "hover:border-neutral-400"}`}
                >
                  <span className="w-5 h-5 shrink-0 border border-neutral-300 bg-[#1A1A1A]" />
                  <span className="font-serif text-base truncate flex-1">{t("ltwTplFromCv")}</span>
                  {!templateId && <Check size={14} strokeWidth={2} className="shrink-0" />}
                </button>
                {templates.map((tpl, i) => (
                  <button
                    key={tpl.id}
                    data-testid={`letter-wizard-template-${i}`}
                    onClick={() => setTemplateId(tpl.id)}
                    className={`ed-card px-4 py-3 text-left flex items-center gap-3 transition-colors ${templateId === tpl.id ? "border-neutral-900 bg-white" : "hover:border-neutral-400"}`}
                  >
                    <span className="w-5 h-5 shrink-0 border border-neutral-300" style={{ background: tpl.design?.accent || "#1A1A1A" }} />
                    <span className="font-serif text-base truncate flex-1">{tpl.name}</span>
                    {templateId === tpl.id && <Check size={14} strokeWidth={2} className="shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-3">
          <p className="text-sm text-neutral-600">{t("ltwHighlightExp")}</p>
          <p className="text-xs font-mono text-neutral-500">{t("ltwOptional")}</p>
          {(resumeDetail?.experiences || []).length === 0 ? (
            <p className="text-sm text-neutral-500 italic font-serif">{t("ltwNoItems")}</p>
          ) : (
            resumeDetail.experiences.map((e, i) => (
              <label
                key={e.id}
                data-testid={`letter-wizard-exp-${i}`}
                className={`ed-card p-4 flex items-start gap-3 cursor-pointer transition-colors ${selExp.includes(e.id) ? "border-neutral-900 bg-white" : "hover:border-neutral-400"}`}
              >
                <input
                  type="checkbox"
                  className="mt-1 w-4 h-4 accent-neutral-900"
                  checked={selExp.includes(e.id)}
                  onChange={() => toggle(selExp, setSelExp, e.id)}
                />
                <span className="min-w-0">
                  <span className="font-serif text-lg block leading-tight">
                    {e.role || "—"}{e.company ? <span className="italic text-neutral-600"> · {e.company}</span> : null}
                  </span>
                  <span className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500">
                    {formatPeriod(e, lang, lang === "en" ? "present" : "heden")}
                  </span>
                </span>
              </label>
            ))
          )}
        </div>
      )}

      {step === 5 && (
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">{t("ltwHighlightSkills")}</p>
          <p className="text-xs font-mono text-neutral-500">{t("ltwOptional")}</p>
          {(resumeDetail?.skills || []).length === 0 ? (
            <p className="text-sm text-neutral-500 italic font-serif">{t("ltwNoItems")}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {resumeDetail.skills.map((s, i) => (
                <button
                  key={s.name}
                  data-testid={`letter-wizard-skill-${i}`}
                  onClick={() => toggle(selSkills, setSelSkills, s.name)}
                  className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${selSkills.includes(s.name) ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]" : "border-neutral-300 hover:border-neutral-900"}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
          {generating && (
            <div className="ed-card p-6 space-y-4" data-testid="letter-wizard-progress">
              <div className="text-center">
                <Sparkles size={18} strokeWidth={1.5} className="mx-auto text-neutral-400 animate-pulse" />
                <p className="ed-eyebrow mt-2" data-testid="letter-wizard-generating">{t("ltwGenerating")}</p>
                <p className="font-mono text-[0.6rem] text-neutral-500 mt-1" data-testid="letter-wizard-elapsed">
                  {t("ltwElapsed").replace("{s}", elapsedSec)}
                </p>
                {autoRetrying && (
                  <p
                    className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-amber-700 mt-1"
                    data-testid="letter-wizard-auto-retry"
                  >
                    {t("ltwAutoRetry")}
                  </p>
                )}
              </div>
              {/* Three-stage progress tracker — visualises WHERE the AI is */}
              <ol className="grid grid-cols-3 gap-2" data-testid="letter-wizard-stages">
                {[
                  { key: "letter", label: t("ltwStageLetter") },
                  { key: "validation", label: t("ltwStageValidation") },
                  { key: "score", label: t("ltwStageScore") },
                ].map(({ key, label }) => {
                  const active = generateStage === key;
                  const done = ["letter", "validation", "score"].indexOf(generateStage) >
                               ["letter", "validation", "score"].indexOf(key);
                  return (
                    <li
                      key={key}
                      data-testid={`letter-wizard-stage-${key}`}
                      data-state={active ? "active" : done ? "done" : "pending"}
                      className={`px-2 py-2 border text-center transition-colors ${
                        active ? "border-neutral-900 bg-neutral-900 text-white" :
                        done ? "border-emerald-600 bg-emerald-50 text-emerald-800" :
                        "border-neutral-200 bg-neutral-50 text-neutral-400"
                      }`}
                    >
                      <span className={`block font-mono text-[0.55rem] uppercase tracking-[0.14em] ${active ? "animate-pulse" : ""}`}>
                        {label}
                      </span>
                    </li>
                  );
                })}
              </ol>
              <div className="flex justify-center">
                <ThinkingLoader stage="letter" testId="letter-wizard-thinking" />
              </div>
            </div>
          )}
          {/* Cloudflare 524 / origin-timeout: friendly recovery panel with
              retry button and helpful copy. Preserves ALL wizard state so
              the user can re-submit without re-selecting anything. */}
          {generateError === "timeout" && !generating && (
            <div
              className="ed-card p-6 border-amber-300 bg-amber-50 space-y-3"
              data-testid="letter-wizard-timeout"
            >
              <p className="ed-eyebrow text-amber-800">{t("ltwTimeoutEyebrow")}</p>
              <p className="text-sm text-neutral-800 leading-relaxed">
                {t("ltwTimeoutBody")}
              </p>
              {errorDetail && (
                <p className="font-mono text-[0.6rem] text-neutral-500 truncate" title={errorDetail}>
                  {errorDetail}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="letter-wizard-retry"
                  onClick={generate}
                  className="ed-btn"
                >
                  <Sparkles size={13} strokeWidth={1.5} /> {t("ltwTimeoutRetry")}
                </button>
                <button
                  type="button"
                  data-testid="letter-wizard-timeout-dismiss"
                  onClick={() => setGenerateError(null)}
                  className="ed-btn ed-btn-outline"
                >
                  {t("cancel")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mt-6 pt-4 border-t border-neutral-200">
        {step > 1 ? (
          <button data-testid="letter-wizard-back" className="ed-btn ed-btn-outline" onClick={() => setStep(step - 1)} disabled={generating}>
            <ArrowLeft size={14} strokeWidth={1.5} /> {t("wizBack")}
          </button>
        ) : <span />}
        {step < 5 ? (
          <button data-testid="letter-wizard-next" className="ed-btn" onClick={next}>
            {t("wizNext")} <ArrowRight size={14} strokeWidth={1.5} />
          </button>
        ) : (
          <button data-testid="letter-wizard-generate" className="ed-btn" onClick={generate} disabled={generating}>
            <Sparkles size={14} strokeWidth={1.5} /> {generating ? t("ltwGenerating") : t("ltwGenerate")}
          </button>
        )}
      </div>
    </WizardModal>
  );
}
